import { AuthService } from './authService';
import { 
  OrdersDto, 
  OrderLineDto, 
  StcTransOrderDto, 
  StcTransOrderLineDto, 
  ItemBinsDto, 
  StcBinDto, 
  DataInspectionSummaryDto 
} from '../types/dataInspection';
import { PListDto, PListLineDto } from '../types/picking';
import { POrdersDto, POrderLineDto } from '../types/porders';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

class DataInspectionService {
  
  // ==============================================
  // SAMMENDRAG / OVERVIEW
  // ==============================================
  
  /**
   * Henter sammendrag av all grunndata
   */
  async getDataSummary(): Promise<DataInspectionSummaryDto> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/summary`);
    
    if (!response.ok) {
      throw new Error(`Failed to get data summary: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  // ==============================================
  // PLUKKLISTER (PLIST)
  // ==============================================
  
  /**
   * Henter alle plukklister
   */
  async getAllPLists(): Promise<PListDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/plists`);
    
    if (!response.ok) {
      throw new Error(`Failed to get PLists: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter plukkliste med linjer
   */
  async getPListWithLines(pListId: number): Promise<{ header: PListDto; lines: PListLineDto[] }> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/plists/${pListId}/lines`);
    
    if (!response.ok) {
      throw new Error(`Failed to get PList lines: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  // ==============================================
  // ORDRE (SALES ORDERS)
  // ==============================================
  
  /**
   * Henter alle salgsordrer
   */
  async getAllOrders(): Promise<OrdersDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/orders`);
    
    if (!response.ok) {
      throw new Error(`Failed to get Orders: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter ordre med linjer
   */
  async getOrderWithLines(orderNo: number): Promise<{ header: OrdersDto; lines: OrderLineDto[] }> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/orders/${orderNo}/lines`);
    
    if (!response.ok) {
      throw new Error(`Failed to get Order lines: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  // ==============================================
  // INNKJØPSORDRE (PORDERS)
  // ==============================================
  
  /**
   * Henter alle innkjøpsordrer
   */
  async getAllPOrders(): Promise<POrdersDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/porders`);
    
    if (!response.ok) {
      throw new Error(`Failed to get POrders: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter innkjøpsordre med linjer
   */
  async getPOrderWithLines(orderNo: number): Promise<{ header: POrdersDto; lines: POrderLineDto[] }> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/porders/${orderNo}/lines`);
    
    if (!response.ok) {
      throw new Error(`Failed to get POrder lines: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  // ==============================================
  // STCTRANSORDERS
  // ==============================================
  
  /**
   * Henter alle StcTransOrders
   */
  async getAllStcTransOrders(): Promise<StcTransOrderDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/stctransorders`);
    
    if (!response.ok) {
      throw new Error(`Failed to get StcTransOrders: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter StcTransOrder med linjer
   */
  async getStcTransOrderWithLines(orderNo: number): Promise<{ header: StcTransOrderDto; lines: StcTransOrderLineDto[] }> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/stctransorders/${orderNo}/lines`);
    
    if (!response.ok) {
      throw new Error(`Failed to get StcTransOrder lines: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  // ==============================================
  // LAGERSALDO (ITEMBINS)
  // ==============================================
  
  /**
   * Henter alle ItemBins
   */
  async getAllItemBins(): Promise<ItemBinsDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/itembins`);
    
    if (!response.ok) {
      throw new Error(`Failed to get ItemBins: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter ItemBins for en spesifikk vare
   */
  async getItemBinsByItemNo(itemNo: string): Promise<ItemBinsDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/itembins/item/${encodeURIComponent(itemNo)}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get ItemBins for item: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter ItemBins for en spesifikk lokasjon
   */
  async getItemBinsByBinCode(binCode: string): Promise<ItemBinsDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/itembins/bin/${encodeURIComponent(binCode)}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get ItemBins for bin: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  // ==============================================
  // LOKASJONER (STCBIN)
  // ==============================================
  
  /**
   * Henter alle StcBins
   */
  async getAllStcBins(): Promise<StcBinDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/stcbins`);
    
    if (!response.ok) {
      throw new Error(`Failed to get StcBins: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter StcBins for et spesifikt lager
   */
  async getStcBinsByWarehouse(warehouseID: string): Promise<StcBinDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/stcbins/warehouse/${encodeURIComponent(warehouseID)}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get StcBins for warehouse: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  /**
   * Henter StcBins for en spesifikk sone
   */
  async getStcBinsByZone(zoneCode: string): Promise<StcBinDto[]> {
    const response = await AuthService.authenticatedGet(`${API_BASE_URL}/data-inspection/stcbins/zone/${encodeURIComponent(zoneCode)}`);
    
    if (!response.ok) {
      throw new Error(`Failed to get StcBins for zone: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }

  // ==============================================
  // SØKEFUNKSJONER
  // ==============================================
  
  /**
   * Universell søkefunksjon
   */
  async searchData(query: string, dataType: string): Promise<any[]> {
    const response = await AuthService.authenticatedGet(
      `${API_BASE_URL}/data-inspection/search?query=${encodeURIComponent(query)}&type=${encodeURIComponent(dataType)}`
    );
    
    if (!response.ok) {
      throw new Error(`Failed to search data: ${response.status} ${response.statusText}`);
    }
    
    return await response.json();
  }
}

export const dataInspectionService = new DataInspectionService();
